import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  const isHomePage = location.pathname === '/';
  const isActive = (path: string) => location.pathname === path;

  const scrollToServices = () => {
    if (location.pathname === '/') {
      // Already on homepage — scroll immediately
      document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Store scroll intent
      localStorage.setItem('scrollTarget', 'services');
      navigate('/');
    }
  };

  const scrollToPartners = () => {
    if (location.pathname === '/') {
      // Already on homepage — scroll immediately
      document.getElementById('partners')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Store scroll intent
      localStorage.setItem('scrollTarget', 'partners');
      navigate('/');
    }
  };

  return (
    <>
      <header className={`fixed top-0 w-full z-50 ${
        isHomePage 
          ? 'bg-black/20 backdrop-blur-sm' // 20% transparent black with blur
          : 'bg-white shadow-md'
      } transition-all duration-300`}>
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            {/* Logo - changes color based on route */}
            <Link to="/" className="flex items-center shrink-0 font-serifDisplay">
              <span className={`text-[33px] ${
                'text-customTeal'
              }`}>nv</span>
              <span className={`text-[33px] ${
                isHomePage ? 'text-white' : 'text-black'
              }`}>/global</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex space-x-8 ml-16">
              <a 
                onClick={scrollToServices}
                className={`${
                  isHomePage ? 'text-white hover:text-gray-300' : 'text-black hover:text-customTeal'
                } transition-colors text-sm cursor-pointer ${
                  isActive('/expertise') ? '!font-bold underline' : 'font-medium'
                }`}
              >
                SERVICES
              </a>
              <a 
                href="/team" 
                className={`${
                  isHomePage ? 'text-white hover:text-gray-300' : 'text-black hover:text-customTeal'
                } transition-colors text-sm ${
                  isActive('/team') ? '!font-bold underline' : 'font-medium'
                }`}
              >
                TEAM
              </a>
              <a 
                onClick={scrollToPartners}
                className={`${
                  isHomePage ? 'text-white hover:text-gray-300' : 'text-black hover:text-customTeal'
                } transition-colors text-sm cursor-pointer ${
                  isActive('/expertise') ? '!font-bold underline' : 'font-medium'
                }`}
              >
                NETWORK PARTNERS
              </a>
              <a 
                href="/insights" 
                className={`${
                  isHomePage ? 'text-white hover:text-gray-300' : 'text-black hover:text-customTeal'
                } transition-colors text-sm ${
                  isActive('/insights') ? '!font-bold underline' : 'font-medium'
                }`}
              >
                INSIGHTS
              </a>
            </nav>

            <div className="flex-grow"></div>

            {/* Contact + Mobile menu toggle */}
            <div className="flex items-center space-x-4">
              <a 
                href="/contact" 
                className={`hidden md:[display:ruby] group ${
                  isHomePage ? 'text-white hover:text-gray-300' : 'text-black hover:text-customTeal'
                } transition-colors text-sm ${
                  isActive('/contact') ? '!font-bold underline' : 'font-medium'
                }`}
              >
                CONTACT
                <svg 
                  className='ml-[6px] group-hover:translate-x-[2.5px] group-hover:translate-y-[-4.33px] transition-transform' 
                  width="9" 
                  height="9" 
                  viewBox="0 0 9 9" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    d="M8.40374 1.02845C8.40374 0.657112 8.10023 0.356083 7.72583 0.356082L1.62459 0.356083C1.25019 0.356083 0.94668 0.657113 0.94668 1.02845C0.94668 1.39979 1.25019 1.70082 1.62459 1.70082L7.04791 1.70082L7.04791 7.07977C7.04791 7.45111 7.35142 7.75214 7.72583 7.75214C8.10023 7.75214 8.40374 7.45111 8.40374 7.07977L8.40374 1.02845ZM0.696777 8L1.17614 8.47544L8.20518 1.50389L7.72583 1.02845L7.24647 0.553015L0.217419 7.52456L0.696777 8Z" 
                    fill={isHomePage ? 'white' : 'black'} 
                  />
                </svg>
              </a>
              <button 
                onClick={() => setMobileMenuOpen(true)} 
                className={`md:hidden ${isHomePage ? 'text-white' : 'text-black'}`}
              >
                ☰
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Slide-Out Menu */}
      <div
        className={`fixed top-0 right-0 h-full w-64 bg-white border-gray-200 backdrop-blur-md border-l transform transition-transform duration-300 z-50 ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex justify-end p-4">
          <button 
            onClick={() => setMobileMenuOpen(false)} 
            className="text-black text-xl"
          >
            ✕
          </button>
        </div>
        <nav className="flex flex-col h-full justify-between">
          <div className="p-6 space-y-6">
            <a 
              onClick={scrollToServices}
              className={`block text-black hover:text-customTeal text-base ${
                isActive('/expertise') ? '!font-bold underline' : 'font-medium'
              }`}
            >
              SERVICES
            </a>
            <a 
              href="/team" 
              className={`block text-black hover:text-customTeal text-base ${
                isActive('/team') ? '!font-bold underline' : 'font-medium'
              }`}
            >
              TEAM
            </a>
            <a 
              onClick={scrollToPartners}
              className={`block text-black hover:text-customTeal text-base ${
                isActive('/expertise') ? '!font-bold underline' : 'font-medium'
              }`}
            >
              NETWORK PARTNERS
            </a>
            <a 
              href="/insights" 
              className={`block text-black hover:text-customTeal text-base ${
                isActive('/insights') ? '!font-bold underline' : 'font-medium'
              }`}
            >
              INSIGHTS
            </a>
          </div>
          <div className="p-6 pb-[75px]">
            <a 
              href="/contact" 
              className={`block text-black hover:text-customTeal text-base ${
                isActive('/contact') ? '!font-bold underline' : 'font-medium'
              }`}
            >
              <div className='[display:ruby]'>
                CONTACT
                <svg className='ml-[6px]' width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.40374 1.02845C8.40374 0.657112 8.10023 0.356083 7.72583 0.356082L1.62459 0.356083C1.25019 0.356083 0.94668 0.657113 0.94668 1.02845C0.94668 1.39979 1.25019 1.70082 1.62459 1.70082L7.04791 1.70082L7.04791 7.07977C7.04791 7.45111 7.35142 7.75214 7.72583 7.75214C8.10023 7.75214 8.40374 7.45111 8.40374 7.07977L8.40374 1.02845ZM0.696777 8L1.17614 8.47544L8.20518 1.50389L7.72583 1.02845L7.24647 0.553015L0.217419 7.52456L0.696777 8Z" 
                    fill="black" 
                  />
                </svg>
              </div>
            </a>
          </div>
        </nav>
      </div>

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Header;